import React from 'react';
import { Award, Heart, Users, Target, BookOpen, Sparkles, Shield } from 'lucide-react';
import { Card } from '../components/ui/card';

export const About = () => {
  const values = [
    {
      icon: Heart,
      title: 'Empati ve Anlayış',
      description: 'Her danışanımıza empatik ve yargısız bir yaklaşımla destek oluruz.'
    },
    {
      icon: Shield,
      title: 'Gizlilik',
      description: 'Paylaştığınız her şey tamamen gizli kalır ve etik kurallara uyarız.'
    },
    {
      icon: Award,
      title: 'Profesyonellik',
      description: 'Sürekli eğitim ve gelişim ile en güncel yaklaşımları uygularız.'
    },
    {
      icon: Users,
      title: 'Kişiye Özel',
      description: 'Her bireyin benzersiz olduğunu bilerek özel çözümler geliştiririz.'
    }
  ];

  const stats = [];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-br from-beige-primary via-beige-secondary to-beige-primary overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-sage-green/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-mint-green/5 rounded-full blur-3xl"></div>
        
        <div className="container relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-block px-4 py-2 bg-sage-green/10 rounded-full mb-6">
              <span className="text-sage-green font-medium text-sm">Hakkımızda</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-forest-green mb-6">
              Sizin İçin Buradayız
            </h1>
            <p className="text-xl text-text-light leading-relaxed">
              Psikolojik danışmanlık alanında yılların deneyimi ve uzman kadromuzla, zihinsel sağlığınız için güvenli ve profesyonel bir destek sunuyoruz.
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="section-padding bg-white">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-forest-green mb-6">
                Misyonumuz ve Vizyonumuz
              </h2>
              <div className="space-y-6 text-text-light text-lg leading-relaxed">
                <p>
                  Bireylerin, çiftlerin ve ailelerin psikolojik sağlıklarını korumak ve geliştirmek için çalışıyoruz. Amacımız, herkesin yaşam kalitesini artıracak profesyonel desteği güvenli bir ortamda alabilmesini sağlamaktır.
                </p>
                <p>
                  Merkez olarak, güncel psikoterapi yaklaşımlarını kullanarak, her danışanımıza özel tedavi planları oluşturuyoruz. Bilimsel temelli yöntemlerle çalışırken, empatik ve destekleyici bir ortam sunmayı önceliğimiz haline getirdik.
                </p>
                <p>
                  Vizyonumuz, toplumda psikolojik sağlığın öneminin daha fazla anlaşılmasını sağlamak ve herkesin ihtiyaç duyduğunda profesyonel desteğe kolayca ulaşabilmesini mümkün kılmaktır.
                </p>
              </div>
            </div>

            <div className="relative">
              <div className="relative z-10 rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.pexels.com/photos/7447058/pexels-photo-7447058.jpeg?w=800&q=80"
                  alt="Kavi Danışmanlık Süreci"
                  className="w-full h-[500px] object-cover"
                />
              </div>
              <div className="absolute -bottom-8 -right-8 w-64 h-64 bg-gradient-to-br from-sage-green/20 to-mint-green/20 rounded-3xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section-padding bg-gradient-to-br from-beige-primary to-white">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-forest-green mb-4">Değerlerimiz</h2>
            <p className="text-lg text-text-light max-w-2xl mx-auto">
              Çalışmalarımızı şekillendiren temel değerlerimiz
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <Card key={index} className="p-8 border-none shadow-lg card-hover bg-white">
                  <div className="w-14 h-14 bg-gradient-to-br from-sage-green to-mint-green rounded-2xl flex items-center justify-center mb-6 transform hover:scale-110 transition-transform duration-300">
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-forest-green mb-3">{value.title}</h3>
                  <p className="text-text-light leading-relaxed">{value.description}</p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Approach Section */}
      <section className="section-padding bg-white">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-forest-green mb-4">Yaklaşımımız</h2>
              <p className="text-lg text-text-light">
                Bilimsel temelli, kişiye özel ve bütünsel bir bakış açısı
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto bg-gradient-to-br from-sage-green to-mint-green rounded-full flex items-center justify-center mb-4">
                  <Target className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-forest-green mb-3">Hedef Odaklı</h3>
                <p className="text-text-light">
                  Danışanlarımızla birlikte net hedefler belirler ve bu hedeflere ulaşmak için adım adım ilerleriz.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 mx-auto bg-gradient-to-br from-sage-green to-mint-green rounded-full flex items-center justify-center mb-4">
                  <BookOpen className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-forest-green mb-3">Bilimsel Temelli</h3>
                <p className="text-text-light">
                  Kanıta dayalı terapötik yaklaşımlar kullanarak, etkinliği kanıtlanmış yöntemlerle çalışırız.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 mx-auto bg-gradient-to-br from-sage-green to-mint-green rounded-full flex items-center justify-center mb-4">
                  <Sparkles className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-forest-green mb-3">Bütünsel Bakış</h3>
                <p className="text-text-light">
                  Kişiyi duygusal, zihinsel ve sosyal yönleriyle bir bütün olarak değerlendiririz.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};